import { Preferences } from '@capacitor/preferences';
import { isNative } from './platformService';

/**
 * Storage Service - Cross-platform storage abstraction
 *
 * Uses Capacitor Preferences on native (iOS/Android) for secure storage.
 * Falls back to localStorage on web for synchronous access.
 *
 * All methods are async for consistency, even when using localStorage.
 */

/**
 * Store a value
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export const setItem = async (key, value) => {
  if (isNative()) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
};

/**
 * Retrieve a value
 * @param {string} key - Storage key
 * @returns {Promise<string|null>} The stored value or null
 */
export const getItem = async (key) => {
  if (isNative()) {
    const { value } = await Preferences.get({ key });
    return value;
  } else {
    return localStorage.getItem(key);
  }
};

/**
 * Remove a value
 * @param {string} key - Storage key
 */
export const removeItem = async (key) => {
  if (isNative()) {
    await Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
};

/**
 * Store multiple values at once
 * @param {Object} items - Object with key-value pairs
 */
export const setItems = async (items) => {
  const promises = Object.entries(items).map(([key, value]) =>
    setItem(key, value)
  );
  await Promise.all(promises);
};

/**
 * Remove multiple values at once
 * @param {string[]} keys - Array of keys to remove
 */
export const removeItems = async (keys) => {
  const promises = keys.map(key => removeItem(key));
  await Promise.all(promises);
};

/**
 * Clear all stored values (use with caution)
 */
export const clear = async () => {
  if (isNative()) {
    await Preferences.clear();
  } else {
    localStorage.clear();
  }
};

// OAuth-specific storage keys
export const STORAGE_KEYS = {
  // Outlook OAuth
  OUTLOOK_STATE: 'outlook_oauth_state',
  OUTLOOK_CODE_VERIFIER: 'outlook_code_verifier',
  OUTLOOK_REDIRECT_MODE: 'outlook_oauth_redirect_mode',
  OUTLOOK_NATIVE_MODE: 'outlook_oauth_native_mode',
  // Google OAuth
  GOOGLE_STATE: 'google_oauth_state',
  GOOGLE_CODE_VERIFIER: 'google_code_verifier',
  GOOGLE_REDIRECT_MODE: 'google_oauth_redirect_mode',
  GOOGLE_NATIVE_MODE: 'google_oauth_native_mode',
};

/**
 * Store OAuth state for a provider
 * @param {'outlook' | 'google'} provider
 * @param {string} state
 * @param {string} codeVerifier
 * @param {boolean} isNativeMode
 * @param {boolean} isRedirectMode
 */
export const storeOAuthState = async (provider, { state, codeVerifier, isNativeMode = false, isRedirectMode = false }) => {
  const prefix = provider === 'outlook' ? 'outlook' : 'google';
  await setItems({
    [`${prefix}_oauth_state`]: state,
    [`${prefix}_code_verifier`]: codeVerifier,
    ...(isNativeMode && { [`${prefix}_oauth_native_mode`]: 'true' }),
    ...(isRedirectMode && { [`${prefix}_oauth_redirect_mode`]: 'true' }),
  });
};

/**
 * Peek at the stored OAuth state without clearing it. Use this when you need to
 * decide which flow to run before consuming. Pair with consumeOAuthState for the
 * actual exchange step (which clears the state).
 * @param {'outlook' | 'google'} provider
 * @returns {Promise<{state: string|null, codeVerifier: string|null, isNativeMode: boolean, isRedirectMode: boolean}>}
 */
export const peekOAuthState = async (provider) => {
  const prefix = provider === 'outlook' ? 'outlook' : 'google';
  const [state, codeVerifier, nativeMode, redirectMode] = await Promise.all([
    getItem(`${prefix}_oauth_state`),
    getItem(`${prefix}_code_verifier`),
    getItem(`${prefix}_oauth_native_mode`),
    getItem(`${prefix}_oauth_redirect_mode`),
  ]);
  return {
    state,
    codeVerifier,
    isNativeMode: nativeMode === 'true',
    isRedirectMode: redirectMode === 'true',
  };
};

/**
 * Retrieve and clear OAuth state for a provider (atomic operation)
 * @param {'outlook' | 'google'} provider
 * @returns {Promise<{state: string|null, codeVerifier: string|null, isNativeMode: boolean, isRedirectMode: boolean}>}
 */
export const consumeOAuthState = async (provider) => {
  const prefix = provider === 'outlook' ? 'outlook' : 'google';

  // Read all values first
  const [state, codeVerifier, nativeMode, redirectMode] = await Promise.all([
    getItem(`${prefix}_oauth_state`),
    getItem(`${prefix}_code_verifier`),
    getItem(`${prefix}_oauth_native_mode`),
    getItem(`${prefix}_oauth_redirect_mode`),
  ]);

  // Clear all values (prevent replay attacks)
  await removeItems([
    `${prefix}_oauth_state`,
    `${prefix}_code_verifier`,
    `${prefix}_oauth_native_mode`,
    `${prefix}_oauth_redirect_mode`,
  ]);

  return {
    state,
    codeVerifier,
    isNativeMode: nativeMode === 'true',
    isRedirectMode: redirectMode === 'true',
  };
};

/**
 * Clear OAuth state for a provider (used on disconnect)
 * @param {'outlook' | 'google'} provider
 */
export const clearOAuthState = async (provider) => {
  const prefix = provider === 'outlook' ? 'outlook' : 'google';
  await removeItems([
    `${prefix}_oauth_state`,
    `${prefix}_code_verifier`,
    `${prefix}_oauth_native_mode`,
    `${prefix}_oauth_redirect_mode`,
  ]);
};

export default {
  setItem,
  getItem,
  removeItem,
  setItems,
  removeItems,
  clear,
  storeOAuthState,
  peekOAuthState,
  consumeOAuthState,
  clearOAuthState,
  STORAGE_KEYS,
};
